import { useEffect, useState } from 'react';
import { SEDE_CODES, bsToUsd, resolveTasa, round2, type TasaPoint } from '@lidotel/shared';
import { api, type FlashExtraction, type ValidationIssue } from '../../lib/api.js';

// `money: true` → cifra en Bs que también se muestra convertida a USD a la tasa
// del día analizado (fecha_operacion). El resto son conteos (sin USD).
const DAY_FIELDS: { key: keyof FlashExtraction['day']; label: string; money?: boolean }[] = [
  { key: 'total_rooms', label: 'Total Rooms' },
  { key: 'ooo', label: 'OOO' },
  { key: 'oos', label: 'OOS' },
  { key: 'ocupadas_sin_ch', label: 'Ocupadas (sin C/H)' },
  { key: 'house', label: 'House' },
  { key: 'comp', label: 'Comp' },
  { key: 'rooms_occupied', label: 'Rooms Occupied' },
  { key: 'adr_bs', label: 'ADR (Bs)', money: true },
  { key: 'room_revenue_bs', label: 'Room Revenue (Bs)', money: true },
  { key: 'total_revenue_bs', label: 'Total Revenue (Bs)', money: true },
  { key: 'fnb_revenue_bs', label: 'F&B Revenue (Bs)', money: true },
  { key: 'revpar_bs', label: 'RevPAR (Bs)', money: true },
];

const emptyForm: FlashExtraction = { sede_codigo: null, fecha_operacion: null, day: {} };

/**
 * Client-side date sanity hint (mirrors the server FECHA_FUTURA/FECHA_LEJANA
 * guard) so a day/month swap by Gemini is visible BEFORE commit.
 */
function fechaAviso(iso: string | null): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  const fecha = new Date(y, m - 1, d);
  const delta = Math.round((fecha.getTime() - today.getTime()) / 86_400_000);
  if (delta > 0) return `La fecha (${iso}) es futura. ¿Día y mes invertidos? Corrige o re-analiza.`;
  if (-delta > 60)
    return `La fecha (${iso}) está a ${-delta} días de hoy. Verifica que el día/mes no esté invertido.`;
  return null;
}

const ISSUE_TONE: Record<ValidationIssue['severity'], string> = {
  error: 'bg-rose-950/50 text-rose-200',
  warning: 'bg-amber-950/50 text-amber-200',
  info: 'bg-slate-900 text-slate-300',
};

export function ReviewPanel({
  id,
  onCommitted,
  onClose,
}: {
  id: string;
  onCommitted: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<FlashExtraction>(emptyForm);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [tasaSeries, setTasaSeries] = useState<TasaPoint[]>([]);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [needsExtract, setNeedsExtract] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track manual edits so re-analyzing can warn before overwriting them.
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let url: string | null = null;
    api
      .ingestaGet(id)
      .then((r) => {
        if (r.extracted && 'day' in r.extracted) {
          setForm({ ...emptyForm, ...r.extracted, day: r.extracted.day ?? {} });
          setDirty(false);
        } else setNeedsExtract(true);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Error'));
    api
      .ingestaFileUrl(id)
      .then((u) => {
        url = u;
        setPdfUrl(u);
      })
      .catch(() => setPdfUrl(null));
    api
      .tasaSeries()
      .then((r) => setTasaSeries(r.series as TasaPoint[]))
      .catch(() => setTasaSeries([]));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [id]);

  // Tasa BCV aplicable al día reportado (exacta o la más cercana anterior, §2.3.1)
  // para mostrar las cifras Bs también en USD.
  const dayRate = form.fecha_operacion
    ? (resolveTasa(tasaSeries, form.fecha_operacion)?.tasa ?? null)
    : null;
  const usdOf = (v: number | null | undefined): string | null => {
    if (dayRate == null || v == null) return null;
    const usd = bsToUsd(v, dayRate);
    return usd == null
      ? null
      : `≈ $${usd.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ADR y RevPAR ya NO se transcriben: el motor los calcula de Room Revenue y
  // habitaciones (inmune a que Gemini lea mal esa celda/columna). Aquí mostramos
  // en vivo el MISMO cálculo — es exactamente lo que se guardará al confirmar.
  const numOf = (v: number | null | undefined): number | null =>
    v == null || !Number.isFinite(v) ? null : v;
  const computedFor = (key: keyof FlashExtraction['day']): number | null => {
    const rev = numOf(form.day.room_revenue_bs);
    if (rev == null) return null;
    if (key === 'adr_bs') {
      const occ = numOf(form.day.ocupadas_sin_ch);
      return occ && occ !== 0 ? round2(rev / occ) : null;
    }
    if (key === 'revpar_bs') {
      const tr = numOf(form.day.total_rooms);
      return tr && tr !== 0 ? round2(rev / tr) : null;
    }
    return null;
  };

  async function extract() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.ingestaExtract(id);
      if ('day' in r.extracted)
        setForm({ ...emptyForm, ...r.extracted, day: r.extracted.day ?? {} });
      setNeedsExtract(false);
      setDirty(false);
      setIssues([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error de extracción');
    } finally {
      setBusy(false);
    }
  }

  /** Re-run Gemini on the same file (e.g. after spotting an inconsistency). */
  async function reanalyze() {
    if (
      dirty &&
      !window.confirm(
        'Re-analizar reemplazará los valores actuales (incluidas tus ediciones) con una nueva lectura de Gemini. ¿Continuar?',
      )
    )
      return;
    await extract();
  }

  function setDay(key: keyof FlashExtraction['day'], raw: string) {
    const value = raw === '' ? null : Number(raw);
    setForm((f) => ({ ...f, day: { ...f.day, [key]: value } }));
    setDirty(true);
  }

  async function commit(force: boolean) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.ingestaCommit(id, form, force);
      setIssues(r.issues ?? []);
      if (r.committed) onCommitted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al confirmar');
    } finally {
      setBusy(false);
    }
  }

  const hasErrors = issues.some((i) => i.severity === 'error');

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-medium text-slate-200">Revisión side-by-side</h2>
        <div className="flex items-center gap-3">
          {!needsExtract && (
            <button
              onClick={reanalyze}
              disabled={busy}
              title="Vuelve a leer el flash con Gemini (útil ante una inconsistencia)"
              className="rounded-md border border-blue-600 px-2.5 py-1 text-xs text-blue-300 hover:bg-blue-950/40 disabled:opacity-50"
            >
              {busy ? 'Re-analizando…' : '↻ Re-analizar con Gemini'}
            </button>
          )}
          <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-200">
            Cerrar ✕
          </button>
        </div>
      </div>

      {error && (
        <p className="mb-3 rounded-lg bg-rose-950/60 px-3 py-2 text-sm text-rose-300">{error}</p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="min-h-[420px] overflow-hidden rounded-lg border border-slate-700 bg-slate-950">
          {pdfUrl ? (
            <iframe src={pdfUrl} title="Flash PDF" className="h-[420px] w-full lg:h-[640px]" />
          ) : (
            <p className="p-4 text-sm text-slate-500">Vista previa no disponible.</p>
          )}
        </div>

        <div>
          {needsExtract ? (
            <div className="mb-4">
              <p className="mb-2 text-sm text-slate-400">Aún no se ha extraído este flash.</p>
              <button
                onClick={extract}
                disabled={busy}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {busy ? 'Extrayendo…' : 'Extraer con Gemini'}
              </button>
            </div>
          ) : null}

          <div className="mb-3 grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-400">
              Sede
              <select
                value={form.sede_codigo ?? ''}
                onChange={(e) => {
                  setForm((f) => ({ ...f, sede_codigo: e.target.value || null }));
                  setDirty(true);
                }}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
              >
                <option value="">—</option>
                {SEDE_CODES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-slate-400">
              Fecha operación
              <input
                type="date"
                value={form.fecha_operacion ?? ''}
                onChange={(e) => {
                  setForm((f) => ({ ...f, fecha_operacion: e.target.value || null }));
                  setDirty(true);
                }}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
              />
            </label>
          </div>

          {fechaAviso(form.fecha_operacion) && (
            <p className="mb-3 rounded-md bg-amber-950/50 px-2 py-1 text-xs text-amber-200">
              ⚠ {fechaAviso(form.fecha_operacion)}
            </p>
          )}

          <p className="mb-2 text-xs text-slate-400">
            {dayRate != null ? (
              <>
                Tasa del día {form.fecha_operacion}:{' '}
                <span className="font-mono text-slate-200">Bs {dayRate.toFixed(4)}</span> · cifras
                en Bs y USD
              </>
            ) : (
              'Sin tasa para la fecha seleccionada — el USD no se puede calcular.'
            )}
          </p>

          <div className="grid grid-cols-2 gap-2">
            {DAY_FIELDS.map((f) => {
              const computed = computedFor(f.key);
              const derived = computed != null && (f.key === 'adr_bs' || f.key === 'revpar_bs');
              const shown = derived ? computed : form.day[f.key];
              const usd = f.money ? usdOf(shown) : null;
              const read = numOf(form.day[f.key]);
              const divergent =
                derived &&
                read != null &&
                Math.abs(read - computed!) / Math.max(Math.abs(computed!), 1) > 0.02;
              return (
                <label key={f.key} className="text-xs text-slate-400">
                  {f.label}
                  {derived && <span className="ml-1 text-sky-400/70">· calculado</span>}
                  <input
                    type="number"
                    step="any"
                    value={shown ?? ''}
                    readOnly={derived}
                    onChange={derived ? undefined : (e) => setDay(f.key, e.target.value)}
                    className={`mt-1 w-full rounded-md border px-2 py-1 text-sm ${
                      derived
                        ? 'border-slate-800 bg-slate-900/40 text-sky-200'
                        : 'border-slate-700 bg-slate-900 text-slate-100'
                    }`}
                  />
                  {f.money && (
                    <span className="mt-0.5 block font-mono text-[11px] text-emerald-300/80">
                      {usd ?? '—'}
                    </span>
                  )}
                  {divergent && (
                    <span className="mt-0.5 block text-[11px] text-amber-300/80">
                      Gemini leyó Bs {read!.toLocaleString('es-VE', { maximumFractionDigits: 2 })}
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          {issues.length > 0 && (
            <ul className="mt-3 space-y-1">
              {issues.map((i, idx) => (
                <li key={idx} className={`rounded-md px-2 py-1 text-xs ${ISSUE_TONE[i.severity]}`}>
                  {i.message}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => commit(false)}
              disabled={busy || needsExtract}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {busy ? 'Confirmando…' : 'Confirmar (commit)'}
            </button>
            {hasErrors && (
              <button
                onClick={() => commit(true)}
                disabled={busy}
                className="rounded-md border border-amber-600 px-3 py-1.5 text-sm text-amber-300 hover:bg-amber-950/40"
              >
                Forzar commit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
